import api from "services/api";
import moment from "moment";
import { RequestPoolData } from "../hooks/useQuickBill";
import { getAccessToken } from "services/accessTokenServices";
import { toast } from "react-toastify";

export interface Unit {
  unitId: string;
  instanceId: number | string;
  condominiumName: string;
  name: string;
  dueDay?: number;
  deliveryType: string;
  hasGasMeter?: boolean;
  hasDirectDebit?: boolean;
  bankId?: string;
  branch?: string;
  account?: string;
  accountCheckDigit?: string;
  accountDocument?: string;
  accountOwnerName?: string;
  hasCurrentHouseholder?: boolean;
  status: string;
  visibleUnit: boolean;
  siteId?: string;
}

interface CustomerAddress {
  siteId: string;
  code: string;
  addressLine: string;
  addressNumber: string;
  complement: string;
  country?: string;
  city: string;
  stateCode: string;
  zipCode: string;
  addressType: string;
  document: string;
  status: string;
  main: boolean;
  unitId: string;
  unit?: Unit;
}

export interface UnitData {
  customerId: number;
  customerCode: string;
  siteCodeShip: string;
  customerSiteId: string;
  customerType: string;
  corporateName: string;
  document: string;
  commercialRegion: string;
  email: string;
  phoneDdd: string;
  phoneNumber: string;
  birthDate: string;
  customerAddresses: CustomerAddress[];
}

interface HistoricalConsumption {
  measurementDate: string;
  initialMeasurement: string;
  finalMeasurement: string;
  m3Consumption: string;
  kgConsumption: string;
}

interface Alert {
  order: string;
  message?: string;
}

export interface BillData {
  id: string;
  externalId: number;
  customerId: number;
  consumerCode: string;
  customerDocument: string;
  customerCode: string;
  customerName: string;
  customerAddress: string;
  invoiceNumber: string;
  emissionDate: string;
  paymentDate: string;
  referenceMonth: string;
  dueDate: string;
  totalAmount: string;
  condominiumName: string;
  condominiumAddress: string;
  condominiumAddressNumber: string;
  unitComplement: string;
  unitId: number;
  alerts: Alert[];
  typeableLine: string;
  status: string;
  historicalConsumptions: HistoricalConsumption[];
}

export type GetBillsResponse = {
  bills: BillData[];
  pageTotal: number;
};

export type FilterParams = {
  beginDate?: string;
  endDate?: string;
  order?: string;
  status?: string;
  condominiumName?: string;
  unitName?: string;
};

export const getBills = async (
  document,
  instanceId,
  page,
  filters?: FilterParams,
  poolToken?: string | null
): Promise<GetBillsResponse> => {
  const accessToken = (await getAccessToken()) || "";
  const headers = {
    access_token: accessToken,
    token: poolToken,
  };

  const { beginDate, endDate, order, status, condominiumName, unitName } =
    filters || {};

  const params = {
    document,
    "unit-id": instanceId,
    _offset: page ? page - 1 : 0,
    _limit: 10,
    ...{ ...(beginDate && { "begin-due-date": beginDate }) },
    ...{ ...(endDate && { "end-due-date": endDate }) },
    ...{ ...(order && { _order_by: order }) },
    ...{ ...(status && { status }) },
    ...{
      ...(condominiumName && { "condominium-name": condominiumName }),
    },
    ...{ ...(unitName && { "unit-name": unitName }) },
  } as any;

  const res = await api.get("industrial/bill/v1/bills", {
    headers,
    params,
  });

  const pageTotal = Number(res.headers["total-pages"]);

  return {
    bills: res.data,
    pageTotal,
  };
};

export const getUnits = async (document: string): Promise<Unit[]> => {
  const accessToken = (await getAccessToken()) || "";
  const headers = {
    access_token: accessToken,
  };

  try {
    const res = await api.get(
      `industrial/customer/v1/customers?type=MI&document=${document}&_expand=UNIT&_view=LEAN`,
      {
        headers,
      }
    );

    if (res.data.length === 0) return [];

    const shipToUnits: UnitData["customerAddresses"][0]["unit"][] =
      res.data.flatMap((customer) =>
        customer.customerAddresses
          .filter(
            (address) => address.addressType === "SHIP_TO" && address.unit
          )
          .map((address) => ({ ...address.unit!, siteId: address?.siteId }))
      );

    return shipToUnits;
  } catch (error) {
    toast.error(error.response?.data?.message || "Erro ao buscar unidades");
    return [];
  }
};

export const getSideIdMain = async (
  document: string
): Promise<UnitData["customerAddresses"][0]["siteId"] | ""> => {
  const accessToken = (await getAccessToken()) || "";
  const headers = {
    access_token: accessToken,
  };

  try {
    const res = await api.get(
      `industrial/customer/v1/customers?type=MI&document=${document}`,
      {
        headers,
      }
    );

    if (res.data.length === 0) return "";

    const [shipToUnits]: UnitData["customerAddresses"] = res.data.flatMap(
      (customer) =>
        customer.customerAddresses.filter(
          (address) =>
            address.addressType === "SHIP_TO" &&
            (address.main === true || address.main === "true")
        )
    );

    return shipToUnits?.siteId ?? "";
  } catch (error) {
    toast.error(error.response?.data?.message || "Erro ao buscar unidades");
    return "";
  }
};

export const sendAnswers = async ({
  document,
  siteId,
  answers,
}: RequestPoolData): Promise<string | null> => {
  const accessToken = (await getAccessToken()) || "";

  const body = {
    document,
    answers,
    siteId,
  };

  const headers = {
    headers: {
      access_token: accessToken,
      "Access-Control-Expose-Headers": "attempts_left",
    },
  };

  try {
    const response = await api.post(
      `industrial/customer/v1/questions/answers`,
      body,
      headers
    );

    const tokenQuestion = response.headers["x-token"];

    return tokenQuestion;
  } catch {
    // Pentest: a mensagem do backend não é exibida (ver QuestionPool).
    return null;
  }
};

export const billDownload = async (
  isReduced: boolean,
  invoice: any,
  installment: any,
  tokenQuetions?: string
): Promise<void> => {
  const accessToken = (await getAccessToken()) || "";
  try {
    const headers = {
      access_token: accessToken,
      "request-origin": "fast-receipt",
    };

    const body = {
      deliveryMethod: "DOWNLOAD",
      isReduced,
      ...(tokenQuetions && { token: tokenQuetions }),
    };

    const response = await api.post(
      `industrial/bill/v1/bills/${invoice.id}`,
      body,
      { headers, responseType: "blob" }
    );
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    const referenceMonth = (installment.referenceMonth || "").split("/");
    link.setAttribute(
      "download",
      `Ultragaz_${moment(`${referenceMonth[1]}/01/${referenceMonth[0]}`)}_${
        invoice.id
      }.pdf`
    );
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  } catch (err) {
    if (err.response) {
      const erro = err.response.data;
      if (erro) {
        const { message } = err.response.data;
        if (err.response.status !== 401) {
          if (message) {
            toast.error(message);
          } else {
            toast.error("Ocorreu um erro interno na aplicação");
          }
        }
      } else if (err.response.status !== 401) {
        toast.error("Ocorreu um erro interno na aplicação");
      }
    } else {
      toast.error("Ocorreu um erro interno na aplicação");
    }
  }
};
